import React, { useState } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import api from '../../../../api';
import './PaymentCollectionTaskPanel.scss';

/** Mirrors `PaymentLinkTaskBase` in dream-be (lib/travelPayments). */
export interface PaymentLinkTaskDetails {
  bookingId: string;
  mandateId: string;
  paymentId: string;
  offerId: string;
  amount: number;
  currency: string;
  cardLast4: string | null;
  cardNetwork: string | null;
  paymentLink: { id: string; url: string; expiresAt: string };
}

interface PaymentCollectionTaskPanelProps {
  taskId: string;
  details: PaymentLinkTaskDetails;
  status: string;
  /** What Dream books once the link is paid, e.g. "the hotel", "the flight". */
  booksWhat: string;
  testId: string;
  readOnly?: boolean;
  onClosed?: () => void;
  /** Vertical-specific warning shown above the actions (e.g. an airline hold). */
  notice?: React.ReactNode;
  /** Vertical-specific `.detail-row`s rendered under the actions. */
  children?: React.ReactNode;
}

const OPEN_STATUSES = new Set(['pending', 'accepted', 'in_progress']);

export function formatMoney(amount: number, currency: string): string {
  try {
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency,
    }).format(amount);
  } catch {
    return `${currency} ${amount.toFixed(2)}`;
  }
}

function errorText(err: unknown): string {
  if (typeof err === 'object' && err !== null && 'response' in err) {
    const data = (err as { response?: { data?: { error?: unknown } } }).response
      ?.data;
    if (data && typeof data.error === 'string') return data.error;
  }
  return err instanceof Error ? err.message : 'Something went wrong';
}

/**
 * Ops panel for a saved-card payment task: the user's approved Book press is
 * waiting for Dream to take the payment from their saved card. Ops reveal the
 * card with the normal "Reveal Card Details" button, pay the internal Airwallex
 * Payment Link with it, and Airwallex's paid webhook completes the booking and
 * this task. The only manual outcome is "Couldn't collect".
 */
const PaymentCollectionTaskPanel: React.FC<PaymentCollectionTaskPanelProps> = ({
  taskId,
  details,
  status,
  booksWhat,
  testId,
  readOnly = false,
  onClosed,
  notice,
  children,
}) => {
  const { getAccessTokenSilently } = useAuth0();
  const [reasonOpen, setReasonOpen] = useState(false);
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [outcome, setOutcome] = useState<string | null>(null);

  const isOpen = OPEN_STATUSES.has(status);
  const linkExpired =
    new Date(details.paymentLink.expiresAt).getTime() < Date.now();
  const card = details.cardLast4
    ? `${details.cardNetwork ? `${details.cardNetwork} ` : ''}•••• ${details.cardLast4}`
    : 'saved card';

  const submitCouldNotCollect = async () => {
    const trimmed = reason.trim();
    if (!trimmed) {
      setError('Say briefly why the payment could not be taken.');
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      const token = await getAccessTokenSilently();
      await api.post(
        `/human-tasks/${taskId}/could-not-collect`,
        { reason: trimmed },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      setOutcome(
        'Closed as could not collect. Nothing was booked; the user has been told and their approval released.',
      );
      setReasonOpen(false);
      onClosed?.();
    } catch (err) {
      setError(errorText(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className='payment-task-panel' data-testid={testId}>
      <div className='payment-task-panel-amount'>
        Take {formatMoney(details.amount, details.currency)} from {card}
      </div>
      <div className='payment-task-panel-steps'>
        <ol>
          <li>Reveal Card Details above to see the user&apos;s saved card.</li>
          <li>
            Open the payment link and pay it with that card (3DS runs on
            Airwallex&apos;s page).
          </li>
          <li>
            Done — once Airwallex confirms the payment Dream books {booksWhat}{' '}
            and this task completes itself. Do not close it by hand.
          </li>
        </ol>
      </div>
      {notice && <div className='payment-task-panel-notice'>{notice}</div>}

      <div className='payment-task-panel-actions'>
        <a
          className={`toolbar-btn payment-task-panel-link${
            !isOpen || linkExpired ? ' disabled' : ''
          }`}
          href={details.paymentLink.url}
          target='_blank'
          rel='noopener noreferrer'
          aria-disabled={!isOpen || linkExpired}
          onClick={(event) => {
            if (!isOpen || linkExpired) event.preventDefault();
          }}
        >
          {linkExpired ? 'Payment link expired' : 'Open payment link'}
        </a>
        {isOpen && !readOnly && !outcome && (
          <button
            type='button'
            className={`toolbar-btn toolbar-btn--close${reasonOpen ? ' active' : ''}`}
            onClick={() => {
              setReasonOpen((open) => !open);
              setError(null);
            }}
            disabled={submitting}
          >
            {reasonOpen ? 'Cancel' : "Couldn't collect"}
          </button>
        )}
      </div>
      <div className='payment-task-panel-hint'>
        Link expires{' '}
        {new Date(details.paymentLink.expiresAt).toLocaleString('en-GB')}. It is
        for ops only — never send it to the user.
      </div>

      {reasonOpen && (
        <div className='payment-task-panel-reason'>
          <label htmlFor={`payment-task-reason-${taskId}`}>
            Why couldn&apos;t the payment be taken?
          </label>
          <textarea
            id={`payment-task-reason-${taskId}`}
            value={reason}
            onChange={(event) => setReason(event.target.value)}
            placeholder='e.g. card declined twice, 3DS failed, card expired'
            disabled={submitting}
            maxLength={500}
          />
          <div className='payment-task-panel-reason-hint'>
            This releases the user&apos;s approval, tells them the booking did
            not go ahead, and closes the task. Nothing is booked.
          </div>
          {error && <div className='payment-task-panel-error'>{error}</div>}
          <div className='payment-task-panel-reason-actions'>
            <button
              type='button'
              className='toolbar-btn toolbar-btn--close'
              onClick={submitCouldNotCollect}
              disabled={submitting}
            >
              {submitting ? 'Closing…' : "Confirm couldn't collect"}
            </button>
          </div>
        </div>
      )}
      {!reasonOpen && error && (
        <div className='payment-task-panel-error'>{error}</div>
      )}
      {outcome && <div className='payment-task-panel-outcome'>{outcome}</div>}

      <div className='task-details'>
        {children}
        <div className='detail-row'>
          <span className='detail-label'>Dream reference:</span>
          <span className='detail-value'>{details.bookingId}</span>
        </div>
      </div>
    </div>
  );
};

export default PaymentCollectionTaskPanel;
